import {formatDisplayDateTime} from '@/dayjs'
import {useTranslation} from '@/translations/i18n'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {AlertCircleIcon, ChevronRightIcon, MessageChatSquareIcon, RefreshCw01Icon} from '@/components/ui/icon/Icon'
import {useGetBugReportIssues} from '../../hooks/useGetBugReportIssues'
import {BugReportIssue} from '../../types'
import {BugReportHistorySkeleton} from './BugReportHistorySkeleton'
import {BugReportIssueDetail} from './BugReportIssueDetail'
import {hasVisibleAssignee, IssueStatus} from './issueState'
import {
    StyledCommentsCount,
    StyledHistoryError,
    StyledHistoryErrorState,
    StyledHistoryFooter,
    StyledHistoryList,
    StyledHistoryRoot,
    StyledHistoryScroll,
    StyledHistoryState,
    StyledIssueCard,
    StyledIssueMeta,
    StyledIssueRow,
    StyledIssueRowMain,
    StyledIssueRowRight,
    StyledIssueTitle,
    StyledRowChevron
} from './style'

const IssueCard = ({issue, onSelect}: {issue: BugReportIssue; onSelect: (issueId: string) => void}) => {
    const {t} = useTranslation()
    const showAssignee = hasVisibleAssignee(issue)

    return (
        <StyledIssueCard>
            <StyledIssueRow type="button" onClick={() => onSelect(issue.id)}>
                <StyledIssueRowMain>
                    <StyledIssueTitle>{issue.title}</StyledIssueTitle>
                    <StyledIssueMeta>
                        <span>{issue.identifier}</span>
                        <span>·</span>
                        <span>{formatDisplayDateTime(issue.createdAt)}</span>
                    </StyledIssueMeta>
                </StyledIssueRowMain>
                <StyledIssueRowRight>
                    {showAssignee && issue.assignee && (
                        <Tooltip content={issue.assignee.name} variant="light" triggerProps={{asChild: true}}>
                            <Avatar
                                name={issue.assignee.name}
                                imageUrl={issue.assignee.avatarUrl ?? undefined}
                                size="xs"
                            />
                        </Tooltip>
                    )}
                    <IssueStatus issue={issue} />
                    {issue.commentCount > 0 && (
                        <StyledCommentsCount
                            role="img"
                            aria-label={t('bug_reporter:comments_count', {count: issue.commentCount})}
                        >
                            <MessageChatSquareIcon size={14} />
                            {issue.commentCount}
                        </StyledCommentsCount>
                    )}
                    <StyledRowChevron>
                        <ChevronRightIcon size={16} />
                    </StyledRowChevron>
                </StyledIssueRowRight>
            </StyledIssueRow>
        </StyledIssueCard>
    )
}

interface BugReportHistoryProps {
    enabled: boolean
    selectedIssueId: string | null
    onSelectIssue: (issueId: string) => void
    onBack: () => void
}

export const BugReportHistory = ({enabled, selectedIssueId, onSelectIssue, onBack}: BugReportHistoryProps) => {
    const {t} = useTranslation()
    const {data: issues, isLoading, isError, error, refetch, isFetching} = useGetBugReportIssues({enabled})
    const selectedIssue = issues?.find(issue => issue.id === selectedIssueId) ?? null

    if (selectedIssue) {
        return <BugReportIssueDetail issue={selectedIssue} onBack={onBack} />
    }

    if (isLoading) {
        return (
            <StyledHistoryRoot>
                <StyledHistoryScroll>
                    <BugReportHistorySkeleton />
                </StyledHistoryScroll>
            </StyledHistoryRoot>
        )
    }

    if (isError) {
        return (
            <StyledHistoryRoot>
                <StyledHistoryScroll>
                    <StyledHistoryErrorState>
                        <StyledHistoryError role="alert">
                            <AlertCircleIcon size={16} />
                            {error instanceof Error ? error.message : t('bug_reporter:history_error')}
                        </StyledHistoryError>
                        <Button type="button" variant="secondary" size="sm" onClick={() => refetch()}>
                            <RefreshCw01Icon size={16} />
                            {t('commons:retry')}
                        </Button>
                    </StyledHistoryErrorState>
                </StyledHistoryScroll>
            </StyledHistoryRoot>
        )
    }

    if (!issues?.length) {
        return (
            <StyledHistoryRoot>
                <StyledHistoryScroll>
                    <StyledHistoryState>
                        <AlertCircleIcon size={24} />
                        {t('bug_reporter:history_empty')}
                    </StyledHistoryState>
                </StyledHistoryScroll>
            </StyledHistoryRoot>
        )
    }

    return (
        <StyledHistoryRoot>
            <StyledHistoryScroll>
                <StyledHistoryList>
                    {issues.map(issue => (
                        <IssueCard key={issue.id} issue={issue} onSelect={onSelectIssue} />
                    ))}
                </StyledHistoryList>
            </StyledHistoryScroll>
            <StyledHistoryFooter>
                <Button type="button" variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    {isFetching ? <Spinner size={16} /> : <RefreshCw01Icon size={16} />}
                    {t('commons:update')}
                </Button>
            </StyledHistoryFooter>
        </StyledHistoryRoot>
    )
}
